import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getSKUs } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { ProductSearchAutocomplete, skuOptionLabel } from '../../../modules/products/public';
import { newClientId } from './catalogCompositionUtils';
import CatalogItemMediaEditor from './CatalogItemMediaEditor';

const KitExternalProductFields = ({ line, disabled, onChange, translate }) => {
  const [skus, setSkus] = useState([]);

  useEffect(() => {
    if (!line.product_id) {
      setSkus([]);
      return;
    }
    (async () => {
      try {
        const res = await getSKUs(line.product_id);
        const list = res?.data?.skus || res?.data || [];
        setSkus(Array.isArray(list) ? list.filter((s) => (s.status || 'ACTIVE').toUpperCase() === 'ACTIVE') : []);
      } catch {
        setSkus([]);
      }
    })();
  }, [line.product_id]);

  return (
    <Box sx={{ mb: 1 }}>
      <ProductSearchAutocomplete
        disabled={disabled}
        productId={line.product_id || null}
        label={translate('catalog.add_product')}
        onChange={(product) => onChange({ product_id: product?.id || '', sku_id: '' })}
        sx={{ mb: 1, width: '100%' }}
      />
      {line.product_id && (
        <FormControl fullWidth size="small">
          <InputLabel>{translate('catalog.variation')}</InputLabel>
          <Select
            label={translate('catalog.variation')}
            value={line.sku_id || ''}
            disabled={disabled}
            onChange={(e) => onChange({ sku_id: e.target.value })}
          >
            {skus.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {skuOptionLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

const CatalogKitItemForm = ({ kit, productItems, onChange, disabled }) => {
  const { translate } = useTranslation();

  if (!kit) return null;

  const updateKit = (patchOrFn) => {
    onChange((prev) => {
      const base = prev ?? kit;
      const patch = typeof patchOrFn === 'function' ? patchOrFn(base) : patchOrFn;
      return { ...base, ...patch };
    });
  };

  const addLine = () => {
    updateKit((base) => ({
      items: [
        ...(base.items || []),
        {
          id: newClientId(),
          catalog_item_id: '',
          sku_id: '',
          product_id: '',
          quantity_mode: 'FIXED',
          quantity: 1,
          min_quantity: null,
          max_quantity: null,
          is_required: true,
          display_order: (base.items || []).length,
        },
      ],
    }));
  };

  const updateLine = (lineIdx, patchOrFn) => {
    onChange((prev) => {
      const base = prev ?? kit;
      return {
        ...base,
        items: (base.items || []).map((ln, i) => {
          if (i !== lineIdx) return ln;
          const patch = typeof patchOrFn === 'function' ? patchOrFn(ln) : patchOrFn;
          return { ...ln, ...patch };
        }),
      };
    });
  };

  const removeLine = (lineIdx) => {
    updateKit((base) => ({
      items: (base.items || []).filter((_, i) => i !== lineIdx),
    }));
  };

  const catalogProductOptions = (productItems || []).map((it) => ({
    id: it.id,
    label: it.display_name || it.product_id,
  }));

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={translate('catalog.kit_name')}
        value={kit.display_name || ''}
        disabled={disabled}
        onChange={(e) => updateKit({ display_name: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        label={translate('catalog.badge_label')}
        value={kit.badge_label || ''}
        disabled={disabled}
        onChange={(e) => updateKit({ badge_label: e.target.value })}
        placeholder={translate('catalog.badge_label_placeholder')}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label={translate('catalog.display_description')}
        value={kit.display_description || ''}
        disabled={disabled}
        onChange={(e) => updateKit({ display_description: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>{translate('catalog.kit_pricing_mode')}</InputLabel>
            <Select
              label={translate('catalog.kit_pricing_mode')}
              value={kit.pricing_mode || 'SUM'}
              disabled={disabled}
              onChange={(e) => updateKit({ pricing_mode: e.target.value })}
            >
              <MenuItem value="SUM">{translate('catalog.kit_pricing_sum')}</MenuItem>
              <MenuItem value="FIXED">{translate('catalog.kit_pricing_fixed')}</MenuItem>
              <MenuItem value="RANGE">{translate('catalog.kit_pricing_range')}</MenuItem>
              <MenuItem value="ON_REQUEST">{translate('catalog.kit_pricing_on_request')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {kit.pricing_mode === 'FIXED' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={translate('catalog.kit_fixed_price')}
              value={kit.fixed_price ?? ''}
              disabled={disabled}
              onChange={(e) =>
                updateKit({ fixed_price: e.target.value === '' ? null : parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        )}
        {kit.pricing_mode === 'RANGE' && (
          <>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={translate('catalog.kit_min_price')}
                value={kit.min_price ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  updateKit({ min_price: e.target.value === '' ? null : parseFloat(e.target.value) })
                }
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={translate('catalog.kit_max_price')}
                value={kit.max_price ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  updateKit({ max_price: e.target.value === '' ? null : parseFloat(e.target.value) })
                }
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('catalog.kit_lines')}
      </Typography>

      {(kit.items || []).map((line, lIdx) => (
        <Paper key={line.id || lIdx} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton size="small" color="error" disabled={disabled} onClick={() => removeLine(lIdx)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>{translate('catalog.kit_line_product')}</InputLabel>
            <Select
              label={translate('catalog.kit_line_product')}
              value={line.catalog_item_id || ''}
              disabled={disabled}
              onChange={(e) => updateLine(lIdx, { catalog_item_id: e.target.value, sku_id: '', product_id: '' })}
            >
              <MenuItem value="">{translate('catalog.kit_line_external')}</MenuItem>
              {catalogProductOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!line.catalog_item_id && (
            <KitExternalProductFields
              line={line}
              disabled={disabled}
              onChange={(patch) => updateLine(lIdx, patch)}
              translate={translate}
            />
          )}
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>{translate('catalog.quantity_mode')}</InputLabel>
            <Select
              label={translate('catalog.quantity_mode')}
              value={line.quantity_mode || 'FIXED'}
              disabled={disabled}
              onChange={(e) => updateLine(lIdx, { quantity_mode: e.target.value })}
            >
              <MenuItem value="FIXED">{translate('catalog.quantity_fixed')}</MenuItem>
              <MenuItem value="RANGE">{translate('catalog.quantity_range')}</MenuItem>
            </Select>
          </FormControl>
          {line.quantity_mode === 'RANGE' ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                type="number"
                label={translate('catalog.quantity_min')}
                value={line.min_quantity ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  updateLine(lIdx, {
                    min_quantity: e.target.value === '' ? null : parseFloat(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label={translate('catalog.quantity_max')}
                value={line.max_quantity ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  updateLine(lIdx, {
                    max_quantity: e.target.value === '' ? null : parseFloat(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
                fullWidth
              />
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              type="number"
              label={translate('catalog.quantity_value')}
              value={line.quantity ?? 1}
              disabled={disabled}
              onChange={(e) => updateLine(lIdx, { quantity: parseFloat(e.target.value) || 1 })}
              inputProps={{ min: 0.01, step: 0.1 }}
              sx={{ mb: 1 }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={line.is_required !== false}
                disabled={disabled}
                onChange={(e) => updateLine(lIdx, { is_required: e.target.checked })}
              />
            }
            label={
              line.is_required !== false
                ? translate('catalog.required_line')
                : translate('catalog.optional_line')
            }
          />
        </Paper>
      ))}

      <Button size="small" startIcon={<AddIcon />} disabled={disabled} onClick={addLine} sx={{ mb: 2 }}>
        {translate('catalog.add_kit_line')}
      </Button>

      <CatalogItemMediaEditor
        item={{ ...kit, id: kit.catalog_item_id || kit._itemId || kit.id }}
        productId=""
        disabled={disabled}
        onChange={onChange}
      />
    </Box>
  );
};

export default CatalogKitItemForm;
